'use client'

import { useEffect } from 'react'
import { RouteErrorPanel } from '@/components/status/route-error-panel'

const CHUNK_RELOAD_KEY = 'hf-chunk-reload-attempt'

// A ChunkLoadError / missing lazy chunk (404) means the browser is holding a
// stale build manifest from before the latest deploy. A one-time hard reload
// fetches the fresh manifest and self-heals instead of stranding the user on a
// dead page. Guard with sessionStorage so a genuinely-broken chunk can't loop.
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name ?? ''} ${error?.message ?? ''}`
  return /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to load chunk|Failed to fetch dynamically imported module/i.test(
    msg,
  )
}

/**
 * The route error boundary: the product's 500 page. What was thrown is for
 * support, not for the reader — it is English and written for a developer
 * ("Cannot read properties of undefined"), so it stays in the console; the
 * page shows the digest Next attaches as a reference support can search for.
 */
export default function RouteError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    console.error(error)
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
          window.location.reload()
          return
        }
      } catch {
        /* private mode / storage disabled — fall through to manual retry */
      }
    } else {
      // Current build renders fine; clear the guard so a future stale-chunk
      // error can self-heal again.
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      } catch {
        /* ignore */
      }
    }
  }, [error])
  return (
    <main className="flex min-h-svh w-full flex-col bg-background">
      <RouteErrorPanel error={error} reset={reset} variant="page" />
    </main>
  )
}
